import React, { useEffect, useState } from 'react';
import { motion, useAnimation, type AnimationControls } from 'framer-motion';
import { App } from '../config/apps';
import TintedIcon from './TintedIcon';

const SPOTIFY_GREEN = '#1DB954';

type TaskbarBtnAccent = 'default' | 'pink' | 'green' | 'violet';

interface TaskbarBtnProps {
  onClick: () => void;
  title: string;
  ariaLabel: string;
  active?: boolean;
  accent?: TaskbarBtnAccent;
  animate?: AnimationControls;
  children: React.ReactNode;
}

const accentActive: Record<TaskbarBtnAccent, string> = {
  default: 'bg-white/15 border-white/25 text-white',
  pink: 'bg-pink-500/20 border-pink-400/40 text-white',
  green: 'bg-[#1DB954]/18 border-[#1DB954]/35 text-white',
  violet: 'bg-violet-500/18 border-violet-400/35 text-white',
};

const TaskbarBtn: React.FC<TaskbarBtnProps> = ({
  onClick,
  title,
  ariaLabel,
  active = false,
  accent = 'default',
  animate,
  children,
}) => (
  <motion.button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={ariaLabel}
    aria-pressed={active}
    animate={animate}
    className={`relative flex h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-xl border transition-colors active:scale-95 ${
      active
        ? accentActive[accent]
        : 'border-white/10 bg-white/[0.07] text-white/75 hover:border-white/20 hover:bg-white/12 hover:text-white'
    }`}
  >
    {children}
    {active && (
      <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-pink-400/90" />
    )}
  </motion.button>
);

const IconSun: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const IconMoon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M21 14.5A8.5 8.5 0 0 1 9.5 3 7 7 0 1 0 21 14.5z" />
  </svg>
);

const IconSpotify: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={SPOTIFY_GREEN} aria-hidden>
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);

const IconFullscreenEnter: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
  </svg>
);

const IconFullscreenExit: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
  </svg>
);

const IconWindowStack: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
    <rect x="3" y="8" width="14" height="10" rx="1.5" />
    <path d="M7 8V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2" />
  </svg>
);

interface TaskbarProps {
  isStartMenuOpen: boolean;
  onStartMenuToggle: () => void;
  isSpotifyWidgetOpen: boolean;
  onSpotifyWidgetToggle: () => void;
  currentTime: Date;
  openWindows: { [key: string]: boolean };
  minimizedWindows: { [key: string]: boolean };
  apps: App[];
  onAppClick: (appId: string) => void;
  onMinimizeWindow: (appId: string) => void;
  onRestoreAllWindows: () => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
}

const Taskbar: React.FC<TaskbarProps> = ({
  isStartMenuOpen,
  onStartMenuToggle,
  isSpotifyWidgetOpen,
  onSpotifyWidgetToggle,
  currentTime,
  openWindows,
  minimizedWindows,
  apps,
  onAppClick,
  onMinimizeWindow,
  onRestoreAllWindows,
  isDarkMode,
  onToggleDarkMode
}) => {
  const activeApps = apps.filter(app => openWindows[app.id]);
  const openAppIds = Object.keys(openWindows).filter(id => openWindows[id]);
  const allWindowsMinimized =
    openAppIds.length > 0 && openAppIds.every(id => minimizedWindows[id]);
  const [isIdle, setIsIdle] = useState(false);
  const controls = useAnimation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const resetIdle = () => {
      setIsIdle(false);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsIdle(true), 5000);
    };

    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('mousedown', resetIdle);
    window.addEventListener('keydown', resetIdle);

    timeoutId = setTimeout(() => setIsIdle(true), 5000);

    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('mousedown', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (isIdle) {
      controls.start({
        scale: [1, 1.05, 1],
        transition: {
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse"
        }
      });
    } else {
      controls.stop();
    }
  }, [isIdle, controls]);

  const handleTaskbarIconClick = (appId: string) => {
    if (minimizedWindows[appId]) {
      // If window is minimized, restore it
      onAppClick(appId);
    } else {
      // If window is open, minimize it
      onMinimizeWindow(appId);
    }
  };

  // Add fullscreen toggle function
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Add fullscreen change event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 flex h-16 sm:h-20 items-center border-t border-white/10 px-2 sm:px-4"
      style={{
        zIndex: 100,
        background: isDarkMode
          ? 'rgba(9, 7, 15, 0.92)'
          : 'rgba(36, 18, 40, 0.88)',
        transition: 'background 0.8s ease-in-out',
      }}
    >
      {/* Left — system controls */}
      <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1">
          <motion.button
            type="button"
            onClick={onStartMenuToggle}
            title="Start menu"
            aria-label="Open start menu"
            aria-expanded={isStartMenuOpen}
            animate={controls}
            className={`relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl border font-mono text-sm font-bold tracking-tight text-white transition-colors active:scale-95 sm:text-base ${
              isStartMenuOpen
                ? 'border-pink-400/45 bg-pink-500/25'
                : 'border-white/15 bg-white/10 hover:border-white/25 hover:bg-white/15'
            }`}
          >
            JM
            {isStartMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-pink-300" />
            )}
          </motion.button>

          <TaskbarBtn
            onClick={onToggleDarkMode}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            ariaLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            active={isDarkMode}
            accent="violet"
          >
            <motion.div
              key={isDarkMode ? 'moon' : 'sun'}
              initial={{ rotate: -40, opacity: 0, scale: 0.75 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              {isDarkMode ? <IconMoon /> : <IconSun />}
            </motion.div>
          </TaskbarBtn>

          <TaskbarBtn
            onClick={onSpotifyWidgetToggle}
            title={isSpotifyWidgetOpen ? 'Hide music widget' : 'Show music widget'}
            ariaLabel={isSpotifyWidgetOpen ? 'Hide music widget' : 'Show music widget'}
            active={isSpotifyWidgetOpen}
            accent="green"
          >
            <IconSpotify />
          </TaskbarBtn>
        </div>
      </div>

      {/* Center section - Active Apps */}
      <div className="flex-1 flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto">
        {activeApps.map(app => (
          <motion.button
            key={app.id}
            data-app-id={app.id}
            onClick={() => handleTaskbarIconClick(app.id)}
            className={`group relative w-9 h-9 sm:w-12 sm:h-12 rounded-lg transition-opacity flex items-center justify-center flex-shrink-0 bg-transparent border-0 active:scale-95 ${
              minimizedWindows[app.id] ? 'opacity-45' : 'opacity-100'
            }`}
          >
            <motion.div
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center"
              animate={isIdle && minimizedWindows[app.id] ? {
                scale: [1, 1.1, 1],
                transition: {
                  duration: 2,
                  repeat: Infinity,
                  repeatType: 'reverse',
                },
              } : {}}
            >
              {typeof app.icon === 'string' && app.icon.includes('.') ? (
                <TintedIcon
                  src={app.icon}
                  alt={app.name}
                  isDarkMode={isDarkMode}
                  className={`h-full w-full transition-[filter] duration-200 ${
                    minimizedWindows[app.id] ? 'drop-shadow-sm' : 'drop-shadow-md'
                  }`}
                />
              ) : (
                <span className="text-xl sm:text-2xl drop-shadow-md">{app.icon}</span>
              )}
            </motion.div>
            {!minimizedWindows[app.id] && (
              <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-pink-400/90" />
            )}
          </motion.button>
        ))}
      </div>

      {/* Right — clock & utilities */}
      <div className="flex flex-shrink-0 items-center gap-2 text-white sm:gap-3">
        <div className="hidden items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1 sm:flex">
          <TaskbarBtn
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            ariaLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            active={isFullscreen}
          >
            {isFullscreen ? <IconFullscreenExit /> : <IconFullscreenEnter />}
          </TaskbarBtn>

          <TaskbarBtn
            onClick={() => {
              if (allWindowsMinimized) {
                onRestoreAllWindows();
              } else {
                openAppIds.forEach(appId => {
                  if (!minimizedWindows[appId]) onMinimizeWindow(appId);
                });
              }
            }}
            title={allWindowsMinimized ? 'Restore all windows' : 'Minimize all windows'}
            ariaLabel={allWindowsMinimized ? 'Restore all windows' : 'Minimize all windows'}
            active={allWindowsMinimized}
            accent="pink"
          >
            <IconWindowStack />
          </TaskbarBtn>
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2.5 py-1.5 sm:px-3">
          <motion.div
            className="text-right leading-tight"
            animate={
              isIdle
                ? {
                    scale: [1, 1.03, 1],
                    transition: { duration: 2, repeat: Infinity, repeatType: 'reverse' },
                  }
                : {}
            }
          >
            <span className="block font-mono text-sm font-semibold tabular-nums sm:text-base">
              {currentTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })}
            </span>
            <span className="hidden font-mono text-[9px] text-white/35 sm:block">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </motion.div>
        </div>

        {/* Mobile-only utility buttons */}
        <div className="flex items-center gap-1 sm:hidden">
          <TaskbarBtn
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            ariaLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            active={isFullscreen}
          >
            {isFullscreen ? <IconFullscreenExit /> : <IconFullscreenEnter />}
          </TaskbarBtn>
          <TaskbarBtn
            onClick={() => {
              if (allWindowsMinimized) {
                onRestoreAllWindows();
              } else {
                openAppIds.forEach(appId => {
                  if (!minimizedWindows[appId]) onMinimizeWindow(appId);
                });
              }
            }}
            title={allWindowsMinimized ? 'Restore all windows' : 'Minimize all windows'}
            ariaLabel={allWindowsMinimized ? 'Restore all windows' : 'Minimize all windows'}
            active={allWindowsMinimized}
            accent="pink"
          >
            <IconWindowStack />
          </TaskbarBtn>
        </div>
      </div>
    </div>
  );
};

export default Taskbar; 